import React, { useEffect, useRef, useState } from 'react';
import useSocketReconnect from '../../utils/useSocketReconnect';

function formatLocal(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  // Convert to local datetime-local input format
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toIsoOrNull(localStr) {
  if (!localStr) return null;
  try {
    const d = new Date(localStr);
    return d.toISOString().slice(0, 19).replace('T', ' '); // MySQL DATETIME compatible
  } catch (_) {
    return null;
  }
}

function MerxandatgeRoundsModal({ socket, item, onClose }) {
  const [loading, setLoading] = useState(false);
  const [deadlines, setDeadlines] = useState([]);
  const [error, setError] = useState('');
  const productId = item?.id;
  const loadedProductId = useRef(null);
  useSocketReconnect(socket, () => {
    if (productId && loadedProductId.current !== productId) socket.emit('.get_merxandatge_item', productId);
  });

  // Load fresh item (to get deadlines/rounds)
  useEffect(() => {
    if (!socket || !productId) return;
    setLoading(true);
    loadedProductId.current = null;
    const handler = (data) => {
      if (data?.id === productId) {
        loadedProductId.current = productId;
        setDeadlines(Array.isArray(data.rounds) ? data.rounds : []);
        setLoading(false);
      }
    };
    socket.on('.merxandatge_item', handler);
    socket.emit('.get_merxandatge_item', productId);
    return () => socket.off('.merxandatge_item', handler);
  }, [socket, productId]);

  const addDraftDeadline = () => {
    setDeadlines(prev => [
      { id: `temp-${Date.now()}`, product_id: productId, starts_at: null, ends_at: '', _isNew: true, _dirty: true },
      ...prev,
    ]);
  };

  const markDirty = (id, patch) => {
    setDeadlines(prev => prev.map(d => d.id === id ? { ...d, ...patch, _dirty: true } : d));
  };

  const saveDeadline = (deadline) => {
    setError('');
    
    // Validate dates
    if (!deadline.ends_at) { 
      setError('Cal definir la data límit'); 
      return; 
    }
    
    const startDate = deadline.starts_at ? new Date(deadline.starts_at) : null;
    const endDate = new Date(deadline.ends_at);
    
    if (startDate && startDate >= endDate) {
      setError('La data d\'obertura ha de ser anterior a la data límit');
      return;
    }
    
    // Check for overlapping deadlines (exclude current one being edited)
    const otherDeadlines = deadlines.filter(d => d.id !== deadline.id);
    const hasOverlap = otherDeadlines.some(existing => {
      const existingStart = existing.starts_at ? new Date(existing.starts_at) : null;
      const existingEnd = existing.ends_at ? new Date(existing.ends_at) : null;
      
      if (!existingEnd) return false;
      
      // For overlap detection, treat missing start dates as very early dates
      const newStart = startDate || new Date(0);
      const newEnd = endDate;
      const existingStartForComparison = existingStart || new Date(0);
      const existingEndForComparison = existingEnd;
      
      // Two periods overlap if one starts before the other ends AND one ends after the other starts
      const overlap = (newStart < existingEndForComparison) && (newEnd > existingStartForComparison);
      
      return overlap;
    });
    
    if (hasOverlap) {
      setError('Aquests períodes se solapen. Les dates límit no poden solapar-se.');
      return;
    }
    
    if (deadline._isNew) {
      // create
      const payload = {
        product_id: productId,
        starts_at: deadline.starts_at ? toIsoOrNull(deadline.starts_at) : null,
        ends_at: toIsoOrNull(deadline.ends_at),
      };
      setLoading(true);
      socket.emit('.create_round', payload, (res) => {
        setLoading(false);
        if (res?.success) {
          // reload
          socket.emit('.get_merxandatge_item', productId);
        } else {
          setError(res?.message || 'Error creant la data límit');
        }
      });
    } else {
      // update
      const payload = {
        id: deadline.id,
        starts_at: deadline.starts_at ? toIsoOrNull(deadline.starts_at) : null,
        ends_at: deadline.ends_at ? toIsoOrNull(deadline.ends_at) : null,
      };
      setLoading(true);
      socket.emit('.update_round', payload, (res) => {
        setLoading(false);
        if (res?.success) {
          socket.emit('.get_merxandatge_item', productId);
        } else {
          setError(res?.message || 'Error actualitzant la data límit');
        }
      });
    }
  };

  const deleteDeadline = (deadline) => {
    if (!window.confirm('Segur que vols eliminar aquesta data límit?')) {
      return;
    }
    
    if (deadline._isNew) {
      // Just remove from local state if it's new
      setDeadlines(prev => prev.filter(d => d.id !== deadline.id));
      return;
    }
    
    // Delete from backend
    setLoading(true);
    setError('');
    socket.emit('.delete_round', { id: deadline.id }, (res) => {
      setLoading(false);
      if (res?.success) {
        // Reload
        socket.emit('.get_merxandatge_item', productId);
      } else {
        setError(res?.message || 'Error eliminant la data límit');
      }
    });
  };

  const DeadlineRow = ({ d }) => (
    <tr key={d.id}>
      <td>
        <input 
          type="datetime-local" 
          value={d.starts_at ? formatLocal(d.starts_at) : ''} 
          onChange={(e) => markDirty(d.id, { starts_at: e.target.value })} 
        />
      </td>
      <td>
        <input 
          type="datetime-local" 
          value={d.ends_at ? formatLocal(d.ends_at) : ''} 
          onChange={(e) => markDirty(d.id, { ends_at: e.target.value })} 
        />
      </td>
      <td>
        <button 
          onClick={() => saveDeadline(d)} 
          disabled={loading || !d._dirty}
          style={{
            padding: '6px 12px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading || !d._dirty ? 'not-allowed' : 'pointer',
            opacity: loading || !d._dirty ? 0.6 : 1
          }}
        >
          Desar
        </button>
      </td>
      <td>
        <button 
          onClick={() => deleteDeadline(d)} 
          disabled={loading}
          style={{
            padding: '6px 12px',
            backgroundColor: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1
          }}
        >
          Eliminar
        </button>
      </td>
    </tr>
  );

  return (
    <div className="merxandatge-form-overlay">
      <div className="merxandatge-form-container">
        <div className="form-header">
          <h2>Data límit: {item?.name}</h2>
          <button className="close-button" onClick={onClose} disabled={loading}>×</button>
        </div>

        <div className="form-content">
          <p style={{ color: '#666', marginBottom: 16, fontSize: '0.9rem' }}>
            Defineix quan s'obre i tanca el període de comandes. Si vols fer una segona comanda, afegeix una nova data límit.
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12 }}>
            <button className="add-variant-button" onClick={addDraftDeadline} disabled={loading}>
              Afegir data límit
            </button>
            {error ? <div className="form-error" style={{ margin: 0 }}>{error}</div> : null}
          </div>
          {loading ? (
            <div className="loading"><div className="spinner"></div><p>Carregant...</p></div>
          ) : (
            <div className="variants-table-container">
              <table className="variants-table">
                <thead>
                  <tr>
                    <th>S'obre el</th>
                    <th>Data límit</th>
                    <th></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {deadlines.length === 0 ? (
                    <tr><td colSpan={4} className="no-variants">No hi ha dates límit configurades</td></tr>
                  ) : (
                    deadlines.map(d => <DeadlineRow key={d.id} d={d} />)
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={onClose} disabled={loading}>Tancar</button>
        </div>
      </div>
    </div>
  );
}

export default MerxandatgeRoundsModal;

